'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Cal, { getCalApi } from '@calcom/embed-react';
import SectionTitle from '../ui/SectionTitle';
import AnimatedSection from '../ui/AnimatedSection';
import { getSoinBySlug } from '@/app/lib/soins';

const CAL_NAMESPACE = 'booking';

interface BookingCalendarProps {
  soinSlug?: string;
}

function BookingCalendar({ soinSlug }: BookingCalendarProps) {
  const calUrl = process.env.NEXT_PUBLIC_CAL_URL;
  const calUsername = process.env.NEXT_PUBLIC_CAL_LINK;
  const selectedSoin = getSoinBySlug(soinSlug ?? '');
  const calLink = selectedSoin ? `${calUsername}/${selectedSoin.slug}` : calUsername;

  useEffect(() => {
    if (!calUrl || !calLink) return;

    (async function () {
      const cal = await getCalApi({
        namespace: CAL_NAMESPACE,
        embedJsUrl: `${calUrl}/embed/embed.js`,
      });
      cal('ui', {
        theme: 'light',
        cssVarsPerTheme: {
          light: { 'cal-brand': '#2f4a3e' },
          dark: { 'cal-brand': '#2f4a3e' },
        },
        hideEventTypeDetails: false,
        layout: 'month_view',
      });
    })();
  }, [calUrl, calLink]);

  return (
    <>
      {calUrl && calLink ? (
        <div className="rounded-2xl overflow-hidden shadow-sm border border-cream-200 bg-white">
          <Cal
            namespace={CAL_NAMESPACE}
            calLink={calLink}
            calOrigin={calUrl}
            embedJsUrl={`${calUrl}/embed/embed.js`}
            style={{ width: '100%', minHeight: '480px' }}
            config={{
              layout: 'month_view',
            }}
          />
        </div>
      ) : (
        /* Placeholder tant que l'agenda cal.diy n'est pas configuré */
        <div className="rounded-2xl border-2 border-dashed border-cream-300 bg-cream-50 p-16 flex flex-col items-center justify-center gap-6 text-center">
          <div className="w-16 h-16 rounded-full bg-forest-lightest/50 flex items-center justify-center text-2xl">
            📅
          </div>
          <div>
            <p className="font-serif text-xl text-forest-dark mb-2">
              Réservation en ligne bientôt disponible
            </p>
            <p className="font-sans text-sm text-forest/55 max-w-sm">
              Notre agenda en ligne sera disponible très prochainement.
              En attendant, contactez-nous directement.
            </p>
          </div>
          <Link
            href="/#contact"
            className="bg-forest text-cream-100 font-sans text-sm px-7 py-3 rounded-full hover:bg-forest-dark transition-colors tracking-wide"
          >
            Nous contacter
          </Link>
        </div>
      )}
    </>
  );
}

interface BookingProps {
  soinSlug?: string;
  title?: string;
  subtitle?: string;
}

export default function Booking({
  soinSlug,
  title = 'Prenez soin de vous',
  subtitle = 'Réservez votre séance en quelques clics, directement depuis notre agenda en ligne.',
}: BookingProps) {
  return (
    <section id="booking" className="py-24 bg-cream-100">
      <div className="max-w-4xl mx-auto px-6">
        <AnimatedSection>
          <SectionTitle eyebrow="Réservation" title={title} subtitle={subtitle} />
        </AnimatedSection>

        <AnimatedSection delay={0.15} className="mt-12">
          <BookingCalendar soinSlug={soinSlug} />
        </AnimatedSection>
      </div>
    </section>
  );
}


